import type { SWRConfiguration } from 'swr';

import useSWR from 'swr';

import type { ApiRequest, ApiEndpoint, ApiResponse, ApiTransport } from './client';

import { sendApiRequest } from './client';

// ----------------------------------------------------------------------

type QueryEndpoint = ApiEndpoint & { method: 'get' | 'post'; response?: never };

type PathValues<E extends QueryEndpoint> =
  NonNullable<ApiRequest<E>['params']> extends { path?: infer Values } ? Partial<Values> : never;

const queryKey = (endpoint: QueryEndpoint, request: { params?: unknown; body?: unknown }) =>
  endpoint.method === 'get'
    ? [endpoint.path, request.params]
    : [endpoint.path, request.params, request.body];

/**
 * useSWR over the JSON reads of one transport: GETs, and POSTs that only read (a search, a count). Pass `null` as the
 * request to hold the fetch (a missing id, a closed panel). The SWR key is `[path, params]`, plus the body for a POST,
 * so revalidate with `mutate(matchApiQuery(endpoint, pathValues))`; transport options stay out of the key.
 */
export function createApiQueryHook<Options>(transport: ApiTransport<Options>) {
  return function useApiQuery<E extends QueryEndpoint>(
    endpoint: E,
    request: ApiRequest<E, Options> | null,
    config?: SWRConfiguration<ApiResponse<E>>
  ) {
    return useSWR(
      request ? queryKey(endpoint, request) : null,
      ([, params, body]) =>
        sendApiRequest<ApiResponse<E>, Options>(transport, endpoint, {
          params,
          body,
          options: request?.options,
        }),
      config
    );
  };
}

/**
 * Matches the SWR keys useApiQuery caches for an endpoint, narrowed to the given path values when passed; any key whose
 * first element is the endpoint's path matches, so a hand-rolled `[path, ...]` key revalidates too.
 *
 * @example mutate(matchApiQuery(endpoints.jobPostingFollowers.getFollowers, { jobId }))
 */
export function matchApiQuery<E extends QueryEndpoint>(endpoint: E, pathValues?: PathValues<E>) {
  return (key: unknown) => {
    if (!Array.isArray(key) || key[0] !== endpoint.path) {
      return false;
    }

    const keyPathValues = (key[1] as { path?: Record<string, unknown> } | undefined)?.path;

    return Object.entries(pathValues ?? {}).every(
      ([name, value]) => keyPathValues?.[name] === value
    );
  };
}
